import { useCallback, useEffect, useMemo, useState } from 'react';

import { checkIfBoostedProjection } from '@/feature/lobby/utils/lineupsUtils';
import { GameMode } from '@/types/api.generated';

import { BetslipPick } from '../types';
import { filterEntryRules } from '../utils/betslip-utils';
import { getGameModeError } from '../utils/game-mode-utils';
import { useBetslipStore } from './use-betslip-store';
import { useEntryRules } from './use-entry-rules';

export const useBetslipData = (_tag?: string) => {
    const betslip = useBetslipStore(state => state.betslip);

    const validationData = useBetslipStore(state => state.validationData);

    const { allEntryRules, canMakeAdditionalPick } = useEntryRules();

    const dynamicMultipliers = useMemo(() => {
        return {
            DYNAMIC:
                validationData.DYNAMIC?.betValidation?.dynamicMultipliers?.filter(
                    it => it.numberOfPicks === betslip.length
                ) ?? [],
        } as const;
    }, [betslip.length, validationData.DYNAMIC?.betValidation?.dynamicMultipliers]);

    const mysteryMultipliers = useMemo(() => {
        const min = validationData.MYSTERY?.betValidation?.minMysteryMultiplier;
        const max = validationData.MYSTERY?.betValidation?.maxMysteryMultiplier;
        if (min && max) {
            return { min, max, multipliers: validationData.MYSTERY.betValidation?.mysteryMultipliers ?? [] };
        }
    }, [
        validationData.MYSTERY.betValidation?.maxMysteryMultiplier,
        validationData.MYSTERY.betValidation?.minMysteryMultiplier,
        validationData.MYSTERY.betValidation?.mysteryMultipliers,
    ]);

    const boostedPicks = useMemo(
        () => betslip.filter(pick => checkIfBoostedProjection(pick.projection.type)),
        [betslip]
    );
    const hasBoostedLineup = boostedPicks.length > 0;

    const dynamicBoostedMultiplier = useMemo(
        () =>
            validationData.DYNAMIC.betValidation?.boostedMultipliers?.find(
                it => it.numberOfPicks === betslip.length && it.numberOfBoostedPicks === boostedPicks.length
            ),
        [betslip.length, boostedPicks.length, validationData.DYNAMIC.betValidation?.boostedMultipliers]
    );

    const perfectRegularMultiplier = validationData.PERFECT.betValidation?.perfectRegularMultiplier;
    const perfectBoostedMultiplier = validationData.PERFECT.betValidation?.perfectBoostedMultiplier;
    const perfectActiveMultiplier = hasBoostedLineup ? perfectBoostedMultiplier : perfectRegularMultiplier;

    const validPicks = useMemo(() => {
        if (betslip.length === 0) {
            return false;
        }

        // go through PERFECT and DYNAMIC game modes and check if it has any INVALID_CRITERIA errors
        // we should look into data with betValidation - this contains the errors
        // make sure we have entry rules for the respective game mode so we don't validate against default validation data (which has no errors)
        const availableBetValidations = [GameMode.Dynamic, GameMode.Perfect]
            .filter(mode => allEntryRules.find(rule => rule.gameMode === mode))
            .map(mode => validationData[mode]);

        const validModes = availableBetValidations.filter(
            data => (data.betValidation?.errors ?? []).filter(it => it.key === 'INVALID_CRITERIA').length === 0
        );

        // if at least 1 game mode is valid, the betslip is valid
        return validModes.length > 0;
    }, [allEntryRules, betslip.length, validationData]);

    const { error: dynamicModeError, disabled: dynamicModeDisabled } = getGameModeError(
        betslip,
        allEntryRules,
        validationData.DYNAMIC.betValidation
    );

    const { error: perfectModeError, disabled: perfectModeDisabled } = getGameModeError(
        betslip,
        allEntryRules,
        validationData.PERFECT.betValidation
    );

    const gameModesErrors = useMemo(() => {
        return {
            [GameMode.Dynamic]: {
                errorMessage: dynamicModeError,
            },
            [GameMode.Perfect]: {
                errorMessage: perfectModeError,
            },
        } as Record<GameMode, { errorMessage?: string }>;
    }, [dynamicModeError, perfectModeError]);

    const isGameModeValid = useCallback(
        (gameMode: GameMode) => {
            switch (gameMode) {
                case GameMode.Perfect:
                    return !perfectModeDisabled && !perfectModeError;
                case GameMode.Dynamic:
                    return !dynamicModeDisabled && !dynamicModeError;
                default:
                    return false;
            }
        },
        [dynamicModeDisabled, dynamicModeError, perfectModeDisabled, perfectModeError]
    );

    const pickslipEntryRules = useMemo(() => filterEntryRules(allEntryRules), [allEntryRules]);

    const edgeCombos = useMemo(
        () =>
            validationData.PERFECT.betValidation?.edgeCombos ?? validationData.DYNAMIC.betValidation?.edgeCombos ?? [],
        [validationData.PERFECT.betValidation?.edgeCombos, validationData.DYNAMIC.betValidation?.edgeCombos]
    );

    return {
        betslip,
        dynamicMultipliers,
        canMakeAdditionalPick,
        /**
         * `true` if the lineup has boosted picks, `false` otherwise
         */
        hasBoostedLineup,
        /**
         * The created edge combos for the lineup, as returned by `validatePicks`
         */
        edgeCombos,
        /**
         * `true` if the lineup has valid picks, and at least 1 game mode is available without errros, `false` otherwise
         */
        validPicks,
        /**
         * `true` if dynamic mode can't be selected due to errors, `false` otherwise
         */
        dynamicModeDisabled,
        dynamicModeError,
        dynamicBoostedMultiplier,
        /**
         * The multiplier for perfect mode without boosted picks, for the current number of picks
         */
        perfectRegularMultiplier,
        /**
         * The multiplier for perfect mode with boosted picks, for the current number of picks
         */
        perfectBoostedMultiplier,
        /**
         * If the lineup has boosted picks, this is the boosted multiplier, otherwise this is the regular multiplier
         */
        perfectActiveMultiplier,
        /**
         * The mystery multipliers data if available, `undefined` otherwise
         */
        mysteryMultipliers,
        /**
         * `true` if perfect mode can't be selected due to errors, `false` otherwise
         */
        perfectModeDisabled,
        perfectModeError,
        pickslipEntryRules,
        gameModesErrors,
        isGameModeValid,
    };
};

// Helper function to check if all picks are from the same team and event
const checkAllPicksFromSameTeam = (picks: BetslipPick[]) => {
    //Don't show the error if there are less than 2 picks
    if (picks.length < 2) return false;

    //Don't show the error if there is at least one pick without team (PGA, UFC, etc) - this means they are definitely not all from same team
    const hasPickWithoutTeam = picks.some(pick => pick.player.team === undefined);
    if (hasPickWithoutTeam) return false;

    const firstPick = picks[0];
    return picks.every(
        pick => pick.player.team?.id === firstPick.player.team?.id && pick.eventId === firstPick.eventId
    );
};

// Returns the previous betslip while the entry is still validating,
// and only returns the new betslip after validation has finished to prevent unnecessary re-renders
export const usePreviousBetslip = () => {
    const betslip = useBetslipStore(state => state.betslip);
    const validating = useBetslipStore(state => state.validating);
    const [previousPicks, setPreviousPicks] = useState<BetslipPick[]>([...betslip]);

    useEffect(() => {
        if (!validating) {
            if (betslip.length !== previousPicks.length) {
                // Only update if the length of the betslip has changed to avoid unnecessary re-renders
                setPreviousPicks([...betslip]);
            }
        }
    }, [betslip, previousPicks.length, validating]);

    const allPicksFromSameTeam = useMemo(() => checkAllPicksFromSameTeam(previousPicks), [previousPicks]);

    return { picks: previousPicks, allPicksFromSameTeam };
};

// Returns the first BE validation error message on the active game mode,
// falling back to Perfect then Dynamic. `undefined` if none has an error.
export const useFirstValidationErrorMessage = () => {
    const activeGameMode = useBetslipStore(state => state.activeGameMode);
    const validationData = useBetslipStore(state => state.validationData);

    return useMemo(() => {
        const firstErrorFor = (mode: GameMode) => validationData[mode]?.betValidation?.errors?.[0];

        return (
            firstErrorFor(activeGameMode)?.message ??
            firstErrorFor(GameMode.Perfect)?.message ??
            firstErrorFor(GameMode.Dynamic)?.message
        );
    }, [activeGameMode, validationData]);
};
